<template>
  <li>
    <button
      class="task-button"
      :class="{ 'task-button--selected': isSelected }"
      @click="emits('click')"
    >
      <div class="task-button__content">
        <div class="task-button__number">
          {{ `${$t('approvals.TASK')} ${taskNumber}` }}
        </div>
        <div class="task-button__description">
          {{ pickTextFromLocale(task.titles) }}
        </div>

        <div class="task-button__attachments">
          <div v-if="task.note" class="attachment">
            <CommentIcon class="attachment__icon" />
            <div class="attachment__text">Comment</div>
          </div>

          <div v-if="task.photo" class="attachment">
            <PictureIcon class="attachment__icon" />
            <div class="attachment__text">{{ $t('approvals.PHOTO') }}</div>
          </div>
        </div>
      </div>
      <ActivityTaskStatusIcon :type="getStatusType" />
    </button>
  </li>
</template>

<script lang="ts" setup>
import CommentIcon from '@/components/icon_components/CommentIcon.vue'
import PictureIcon from '@/components/icon_components/PictureIcon.vue'
import { useContentLocale } from '@/hooks/useContentLocale'
import type { ActivityTask } from '@/types'
import { computed, defineEmits, defineProps } from 'vue'
import ActivityTaskStatusIcon from '../activity/ActivityTaskStatusIcon.vue'

const props = defineProps<{
  task: ActivityTask
  taskNumber: number
  isSelected: boolean
}>()

const emits = defineEmits<{
  (e: 'click'): void
}>()

const { pickTextFromLocale } = useContentLocale('-')

const getStatusType = computed(() => {
  if (props.task.rejected) {
    return 'rejected'
  }
  return props.task.done ? 'completed' : 'not-completed'
})
</script>

<style lang="scss" scoped>
.task-button {
  display: flex;
  gap: $spacing-2xs;
  padding: $spacing-2xs $spacing-xs;
  width: 100%;
  border: 1px solid transparent;
  border-left: 4px solid transparent;
  border-bottom: 1px solid $color-border;
  background: none;

  &:hover {
    background-color: $color-pebble;
    cursor: pointer;
  }

  &--selected {
    background-color: $color-blue-05;
    border-left-color: $color-navigation;

    .task-button__description {
      font-weight: 700;
    }

    &:hover {
      background-color: $color-blue-05;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $spacing-5xs;
    overflow: hidden;
    width: 100%;
    text-align: left;
  }

  &__number {
    @include formLabelStyle;
    margin-bottom: 0;
    color: $color-text-light;
  }

  &__description {
    @include ellipsis;
    margin-bottom: $spacing-4xs;
    color: $color-text;
  }

  &__attachments {
    display: flex;
    gap: $spacing-3xs;
  }
}

.attachment {
  display: flex;
  align-items: center;
  gap: $spacing-5xs;
  color: $color-text-light;

  &__icon {
    width: 14px;
    height: 14px;
  }

  &__text {
    font-size: $font-size-s;
  }
}
</style>
